// src/pages/offlineSales/terminal/components/ProductGrid.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ScanBarcode, RotateCw } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { searchProducts } from '../../../../services/api/productSearch';
import { getProductCategories } from '../../../../services/api/categories';
import { getBrands } from '../../../../services/api/brands';
import Dropdown from '../../../../components/ui/Dropdown/Dropdown';
import { getImageUrl } from '../../../../utils/imageUtils';
import styles from '../OfflineSalesTerminal.module.css';

const NO_IMAGE_SRC = '/assets/other/no-image-preview.png';

// Swap broken product images for the placeholder (guarded to avoid an error loop)
const handleNoImage = (e) => {
  if (!e.currentTarget.src.endsWith(NO_IMAGE_SRC)) {
    e.currentTarget.src = NO_IMAGE_SRC;
  }
};

const SORT_OPTIONS = [
  { value: 'priority', label: 'Priority' },
  { value: 'name_asc', label: 'Name (A - Z)' },
  { value: 'name_desc', label: 'Name (Z - A)' },
  { value: 'price_asc', label: 'Price (Low - High)' },
  { value: 'price_desc', label: 'Price (High - Low)' },
];

// The categories API returns a tree (roots with nested `children`) — flatten it for the dropdown,
// indenting sub-categories so the hierarchy stays readable
const flattenCategories = (nodes, depth = 0) =>
  nodes.flatMap(c => [
    { id: c.id, name: `${'— '.repeat(depth)}${c.name}` },
    ...flattenCategories(c.children || [], depth + 1),
  ]);

const sortProducts = (list, sortBy) => {
  const sorted = [...list];
  switch (sortBy) {
    case 'name_desc': return sorted.sort((a, b) => (b.displayName || '').localeCompare(a.displayName || ''));
    case 'price_asc': return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    case 'price_desc': return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    case 'name_asc': return sorted.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
    // 'priority': keep the server order (priority 1 first, then unprioritised A–Z)
    default: return sorted;
  }
};

const ProductGrid = ({ shopId, onAddToCart }) => {
  const { t, formatCurrency } = useLocalization();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const debounceRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      loadProducts('');
      return;
    }
    debounceRef.current = setTimeout(() => loadProducts(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadProducts(query);
  }, [shopId, categoryId, brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getProductCategories({ limit: 1000 })
      .then(res => setCategories(flattenCategories(res.categories || [])))
      .catch(() => {});
    getBrands()
      .then(res => setBrands(res.brands || []))
      .catch(() => {});
  }, []);

  const loadProducts = async (search) => {
    try {
      setLoading(true);
      // TODO: backend /product-search/shop-products does not filter by categoryId / brandId yet
      const result = await searchProducts(shopId, search, {
        limit: 48,
        orderBy: 'priority',
        ...(categoryId && { categoryId }),
        ...(brandId && { brandId }),
      });
      setProducts(result.products || result.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const sortedProducts = useMemo(() => sortProducts(products, sortBy), [products, sortBy]);

  const handleSearchKeyDown = (e) => {
    // Enter on a barcode/SKU search — try exact match
    if (e.key === 'Enter' && query.trim()) {
      const exact = products.find(p =>
        p.sku === query.trim() || p.barcode === query.trim()
      );
      if (exact) {
        onAddToCart(exact);
        setQuery('');
      }
    }
  };

  return (
    <div className={styles.productGrid}>
      <div className={styles.productFilters}>
        <div className={styles.productSearchRow}>
          <div className={styles.searchBox}>
            <Search size={18} className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search products or scan barcode / item code..."
              className={styles.searchInput}
              autoFocus
            />
          </div>
          <button
            type="button"
            className={styles.scanBtn}
            onClick={() => searchInputRef.current?.focus()}
            title="Scan barcode"
          >
            <ScanBarcode size={20} />
          </button>
        </div>

        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <Dropdown
              value={categoryId}
              onChange={value => setCategoryId(value || '')}
              options={categories.map(c => ({ value: c.id, label: c.name }))}
              placeholder="- All Categories -"
              searchable
              allowClear={false}
              className={styles.filterDropdown}
            />
            <button
              type="button"
              className={styles.filterResetBtn}
              onClick={() => setCategoryId('')}
              title="Reset category"
            >
              <RotateCw size={16} />
            </button>
          </div>
          <div className={styles.filterGroup}>
            <Dropdown
              value={brandId}
              onChange={value => setBrandId(value || '')}
              options={brands.map(b => ({ value: b.id, label: b.name }))}
              placeholder="- All Brands -"
              searchable
              allowClear={false}
              className={styles.filterDropdown}
            />
            <button
              type="button"
              className={styles.filterResetBtn}
              onClick={() => setBrandId('')}
              title="Reset brand"
            >
              <RotateCw size={16} />
            </button>
          </div>
          <div className={styles.filterGroup}>
            <Dropdown
              value={sortBy}
              onChange={value => setSortBy(value || 'priority')}
              options={SORT_OPTIONS}
              allowClear={false}
              className={styles.filterDropdown}
            />
            <button
              type="button"
              className={styles.filterResetBtn}
              onClick={() => setSortBy('priority')}
              title="Reset sort"
            >
              <RotateCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.gridLoading}>...</div>
      ) : products.length === 0 ? (
        <div className={styles.gridEmpty}>{t('pos.noProductsFound')}</div>
      ) : (
        <div className={styles.productTiles}>
          {sortedProducts.map(product => (
            <button
              key={`${product.productId}-${product.variationId || 'base'}`}
              className={styles.productTile}
              onClick={() => onAddToCart(product, product.variationId || null, product.attributeDisplay || null)}
            >
              <div className={styles.tileImageWrap}>
                <img
                  src={product.image ? getImageUrl(product.image) : NO_IMAGE_SRC}
                  alt={product.displayName}
                  className={styles.tileImage}
                  onError={handleNoImage}
                />
              </div>
              <span className={styles.tileName}>{product.displayName}</span>
              {product.attributeDisplay && !product.displayName?.includes(product.attributeDisplay) && (
                <span className={styles.tileVariation}>{product.attributeDisplay}</span>
              )}
              <span className={styles.tilePrice}>{formatCurrency(product.price)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
